import { DataPointTrace } from '../data-points/types';

/**
 * The dimension along which company operations are broken down.
 * Each datapoint represents operations for either a specific location (country) or a specific industry.
 */
export enum CompanyOperationsDimension {
  COUNTRY = 'country',
  INDUSTRY = 'industry',
}

/**
 * The type of operational metric being measured.
 * Each datapoint represents one metric (e.g., revenue, suppliers, employees) for a specific location or industry.
 */
export enum CompanyOperationsMetricType {
  REVENUE = 'revenue',
  SUPPLIERS = 'suppliers',
  EMPLOYEES = 'employees',
  PRODUCTION_SITES = 'production_sites',
}

/**
 * A list of industries the company could operate in as of 12.1.2026. Currently used in the operational data
 * input features in the DMA side.
 */
export enum CompanyIndustry {
  AGRICULTURE = 'Agriculture',
  CONSTRUCTION = 'Construction',
  ENERGY = 'Energy',
  FORESTRY = 'Forestry',
  FREIGHT = 'Freight',
  INSURANCE = 'Insurance',
  MINING = 'Mining',
  PESTICIDES = 'Pesticides',
  PLASTIC = 'Plastic',
  REAL_ESTATE = 'Real estate',
  SANITATION = 'Sanitation',
  TEXTILES = 'Textiles',
  TOBACCO = 'Tobacco',
  UTILITIES = 'Utilities',
  VEHICLES = 'Vehicles',
  VICES = 'Vices',
}

export const industryDescriptionByIndustry: Record<CompanyIndustry, string> = {
  [CompanyIndustry.AGRICULTURE]: 'Growing crops and raising animals',
  [CompanyIndustry.CONSTRUCTION]: 'Building and infrastructure work',
  [CompanyIndustry.ENERGY]: 'Supplying electricity or gas',
  [CompanyIndustry.FORESTRY]: 'Logging and forest management',
  [CompanyIndustry.FREIGHT]: 'Moving goods by road, rail, water or air',
  [CompanyIndustry.INSURANCE]: 'Insurance and pension services',
  [CompanyIndustry.MINING]: 'Extracting minerals, metals and raw materials, as well as fossil fuel extraction',
  [CompanyIndustry.PESTICIDES]: 'Making pesticides',
  [CompanyIndustry.PLASTIC]: 'Making plastic products',
  [CompanyIndustry.REAL_ESTATE]: 'Buying, selling or renting property',
  [CompanyIndustry.SANITATION]: 'Water, wastewater, waste and cleanup services',
  [CompanyIndustry.TEXTILES]: 'Making textiles and clothing',
  [CompanyIndustry.TOBACCO]: 'Producing tobacco and related products',
  [CompanyIndustry.UTILITIES]: 'Electricity, gas, steam and air conditioning supply',
  [CompanyIndustry.VEHICLES]: 'Manufacturing of motor vehicles',
  [CompanyIndustry.VICES]: 'Gambling, weapons, alcohol and similar harmful products',
};

/**
 * Base interface for a single datapoint representing company operations.
 * Each datapoint contains one piece of information about where a company operates (location or industry)
 * for a specific operational metric (revenue, suppliers, employees, etc.).
 */
interface CompanyOperationsPointBase {
  id: number;
  companyId: string;
  year: number;
  weight: number;
  source: string;
  trace: DataPointTrace;
  operationsMetricType: CompanyOperationsMetricType;
  createdBy: number;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * A datapoint representing company operations for a specific industry.
 */
export type CompanyIndustryOperationsPoint = CompanyOperationsPointBase & {
  dimension: CompanyOperationsDimension.INDUSTRY;
  key: CompanyIndustry;
};

/**
 * A datapoint representing company operations for a specific location (country).
 */
export type CompanyGeographicalOperationsPoint = CompanyOperationsPointBase & {
  dimension: CompanyOperationsDimension.COUNTRY;
  key: string;
};

/**
 * A single datapoint representing company operations.
 * Each point contains information about operations for either a specific location (country) or industry,
 * measured by a specific metric type (revenue, suppliers, employees, etc.).
 */
export type CompanyOperationsPoint = CompanyIndustryOperationsPoint | CompanyGeographicalOperationsPoint;
